import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  InputAdornment,
  TextFieldProps,
  CircularProgress,
  DialogActions,
  Box,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useTranslate } from "src/locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import Tooltip from "@mui/material/Tooltip";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { baseService } from "src/api/core/baseService";
import { MutationResponse } from "src/modules/nurse/patient-list/pages/list/models";
import { GridToolbar, GridToolbarContainer, GridToolbarQuickFilter } from "@mui/x-data-grid-premium";
import { GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid-premium";
import ByzMuiDeleteButton from 'src/components/core-components/mui/buttons/ByzMuiDeleteButton';
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";


export type ByzIsolationSelectListPopupInputProps = TextFieldProps & {
  url: string;
  method?: "GET" | "POST";
  postData?: any;
  label?: string;
  dialogTitle?: string;
  dialogShowOkButton?: boolean;
  onDialogOkClick?: () => void;
  onChange?: (value: any) => void;
  dialogOpen?: boolean;
  setDialogOpen?: (open: boolean) => void;
  displayField?: string;
  columns?: any[];
  selectedId?: string | number | null;
};

const ByzIsolationSelectListPopupInput: React.FC<ByzIsolationSelectListPopupInputProps> = ({
  url,
  method = "GET",
  postData,
  label,
  dialogTitle,
  dialogShowOkButton = true,
  onDialogOkClick,
  onChange,
  dialogOpen,
  setDialogOpen,
  displayField = "NAME",
  columns,
  value,
  selectedId: externalSelectedId,
  ...rest
}) => {
  const { t } = useTranslate();
  const apiRef = useGridApiRef();
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState<any>(value || "");
  const [data, setData] = useState<any[]>([]);
  const [selectionModel, setSelectionModel] = useState<string[]>([]);
  const latestSelectionRef = useRef<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelectionChange = (ids: GridRowSelectionModel) => {
    const id = Array.isArray(ids) && ids.length > 0 ? ids[0] : null;
    setSelectedId(id);
    latestSelectionRef.current = ids as string[];
    setSelectionModel(ids as string[]);
  };

  useEffect(() => {
    console.log("Selection model updated:", selectionModel);
  }, [selectionModel]);

  const isOpen = dialogOpen !== undefined ? dialogOpen : internalOpen;

  const handleOpen = () => {
    // Mevcut seçimi (id) varsa, grid açıldığında onu seçili yap
    if (externalSelectedId !== undefined && externalSelectedId !== null && externalSelectedId !== "") {
      const idStr = String(externalSelectedId);
      setSelectedId(idStr);
      setSelectionModel([idStr]);
      latestSelectionRef.current = [idStr];
    } else {
      setSelectedId(null);
      setSelectionModel([]);
      latestSelectionRef.current = [];
    }
    if (setDialogOpen) setDialogOpen(true);
    else setInternalOpen(true);
  };

  const handleClose = () => {
    if (setDialogOpen) setDialogOpen(false);
    else setInternalOpen(false);
  };

  const defaultColumns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "NAME", headerName: "Adı", flex: 1 },
  ];
  const dataColumns = columns ?? defaultColumns;

  // Dışarıdan gelen value değiştiğinde input'u senkronize et
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // externalSelectedId değiştiğinde ve data yüklendiğinde seçimi güncelle
  useEffect(() => {
    if (externalSelectedId !== undefined && externalSelectedId !== null && externalSelectedId !== "" && data.length > 0) {
      const idStr = String(externalSelectedId);
      const foundRow = data.find((row: any) => String(row.id) === idStr);
      if (foundRow) {
        setSelectedId(idStr);
        setSelectionModel([idStr]);
        latestSelectionRef.current = [idStr];
      }
    } else if (externalSelectedId === null || externalSelectedId === undefined || externalSelectedId === "") {
      setSelectedId(null);
      setSelectionModel([]);
      latestSelectionRef.current = [];
    }
  }, [externalSelectedId, data.length]);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);
    const fetchData = async () => {
      try {
        let res: MutationResponse | undefined;
        if (method === "GET") {
          res = await baseService.getAll<MutationResponse>(url);
        } else {
          res = await baseService.add<MutationResponse>(url, postData);
        }
        // PATCH: result.data desteği
        let rawList: any[] = [];
        if (res?.DbResponse?.Result) {
          rawList = res.DbResponse.Result;
        } else if ((res as any)?.result?.data) {
          rawList = (res as any).result.data;
        } else if ((res as any)?.data) {
          rawList = (res as any).data;
        }
        const result = (rawList ?? []).map((row: any) => ({
          ...row,
          id: String(row.ID ?? row.id),
        }));
        console.log('ByzSelectListPopupInput result:', result);
        setData(result);
        
        // Data yüklendikten sonra externalSelectedId'ye göre seçimi yap
        if (externalSelectedId !== undefined && externalSelectedId !== null && externalSelectedId !== "") {
          const idStr = String(externalSelectedId);
          const foundRow = result.find((row: any) => String(row.id) === idStr);
          if (foundRow) {
            setSelectedId(idStr);
            setSelectionModel([idStr]);
            latestSelectionRef.current = [idStr];
          }
        }
      } catch (err) {
        console.error("Veriyi çekerken hata oluştu:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, url, method, postData, externalSelectedId]);

  const handleDialogOkClickInternal = () => {
    if (selectedId) {
      const selectedRow = data.find(row => row.id === selectedId);
      if (selectedRow) {
        onChange?.(selectedRow);
        setInputValue(selectedRow[displayField]);
      }
    }
    onDialogOkClick?.();
    handleClose();
  };

  return (
    <>
      <Tooltip title={inputValue || ""} disableHoverListener={!inputValue}>
        <span>
          <ByzMuiTextField
            {...rest}
            value={inputValue}
            label={label || t("buttons.chose")}
            variant="outlined"
            color="primary"
            inputProps={{
              readOnly: true,
              style: { cursor: "pointer", height: 45, boxSizing: "border-box" }
            }}
            onClick={handleOpen}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen();
                    }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              borderColor: "#2196F3",
              "& .MuiInputBase-input": {
                height: 45,
                padding: "0 14px",
                boxSizing: "border-box",
              },
            }}
          />
        </span>
      </Tooltip>

      <OpenComponentDialog
        title={dialogTitle || t("buttons.chose")}
        open={isOpen}
        showOkButton={false}
        onClose={handleClose}
        maxWidth="md"
        fullWidth={false}
      >
        <DialogActions sx={{ mb: 2 }}>
          <ByzMuiDeleteButton
            onClick={() => {
              setSelectedId(null);
              setInputValue('');
              onChange?.(null);
              handleClose();
            }}
          />
          <ByzNurseSaveButton onClick={handleDialogOkClickInternal}
            sx={{
              width: 100,
              height: 40,
              fontSize: 14,
              fontWeight: 600,
            }}
          />
        </DialogActions>
        <Box>
          {loading ? (
            <CircularProgress />
          ) : (
            <ByzMuiDataGrid
              apiRef={apiRef}
              rows={data}
              columns={dataColumns}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(ids) => {
                // Sadece tekli seçim olsun
                const idArr = Array.isArray(ids) ? ids : [ids];
                const singleId = idArr.length > 0 ? [idArr[idArr.length - 1]] : [];
                handleSelectionChange(singleId);
              }}
            />
          )}
        </Box>
      </OpenComponentDialog>
    </>
  );
};

export default ByzIsolationSelectListPopupInput; 